/**
 * Snapshot interface for command-pattern undo/redo.
 *
 * Each snapshot is a toggle: calling restore() flips between two states.
 * The manager pushes the SAME snapshot object between undo and redo stacks —
 * no new snapshot objects are created during undo/redo.
 */
export interface Snapshot {
  restore(): Snapshot;
}

/**
 * ToolUndoRedoManager
 *
 * Command-pattern undo/redo manager for persona tools.
 * Stores self-restoring Snapshot objects — the manager never needs
 * to know what kind of state each snapshot contains.
 */
export class ToolUndoRedoManager {
  private undoStack: Snapshot[] = [];
  private redoStack: Snapshot[] = [];
  private static readonly MAX_ENTRIES = 50;

  /**
   * Guard against reentrant calls. snapshot.restore() dispatches synchronous
   * scene events whose handlers could trigger pushState (which clears the redo
   * stack) or another undo/redo. The flag prevents stack corruption in those cases.
   */
  private restoring = false;

  /** Optional callback fired whenever the stacks change (for UI updates). */
  onStackChanged?: () => void;

  /**
   * Push a snapshot onto the undo stack.
   * Clears the redo stack (new action invalidates forward history).
   */
  pushState(snapshot: Snapshot): void {
    if (this.restoring) return;
    this.undoStack.push(snapshot);
    this.redoStack = [];
    if (this.undoStack.length > ToolUndoRedoManager.MAX_ENTRIES) {
      this.undoStack.shift();
    }
    this.onStackChanged?.();
  }

  /**
   * Undo: pops the previous snapshot, restores it (toggling its internal state),
   * then pushes the SAME snapshot onto the redo stack.
   */
  undo(): boolean {
    if (this.restoring) return false;
    const snapshot = this.undoStack.pop();
    if (!snapshot) return false;
    this.restoring = true;
    try {
      snapshot.restore();
    } catch (err) {
      console.error('[UndoRedo] undo restore() threw:', err);
      this.undoStack.push(snapshot);
      return false;
    } finally {
      this.restoring = false;
    }
    this.redoStack.push(snapshot);
    this.onStackChanged?.();
    return true;
  }

  /**
   * Redo: pops the next snapshot, restores it (toggling its internal state),
   * then pushes the SAME snapshot onto the undo stack.
   */
  redo(): boolean {
    if (this.restoring) return false;
    const snapshot = this.redoStack.pop();
    if (!snapshot) return false;
    this.restoring = true;
    try {
      snapshot.restore();
    } catch (err) {
      console.error('[UndoRedo] redo restore() threw:', err);
      this.redoStack.push(snapshot);
      return false;
    } finally {
      this.restoring = false;
    }
    this.undoStack.push(snapshot);
    this.onStackChanged?.();
    return true;
  }

  /**
   * Peek at the top snapshot on the undo stack without removing it.
   */
  peekUndo(): Snapshot | undefined {
    return this.undoStack[this.undoStack.length - 1];
  }

  /**
   * Discard the most recent undo snapshot (e.g. when a drag is cancelled).
   */
  popState(): void {
    this.undoStack.pop();
    this.onStackChanged?.();
  }

  /**
   * Clear all undo and redo history.
   */
  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.onStackChanged?.();
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
}
